import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Typography,
  Box,
  Tooltip,
  TextField,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Edit, Delete, Check, Close } from '@mui/icons-material';
import { StyledTableCell, StyledTableRow } from '../Pedidos/components/acesso/acessoIndex';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../Api/apiAxios';
import { MyContext } from '../../Context/MyContext';
import Mensagem from '../../Components/Mensagem';

const validationSchema = Yup.object({
  tituloTipo: Yup.string().required('Obrigatório'),
  codigoServicoSisdm: Yup.number()
    .typeError('Número inválido')
    .required('Obrigatório')
    .integer('Inteiro')
    .min(1, 'Mín. 1')
    .max(99999, 'Máx. 99999'),
});

export function TipoServico() {
  const [tiposServico, setTiposServico] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const { mensagemGlobal, setMensagemGlobal, tipoMensagemGlobal, setTipoMensagemGlobal } = useContext(MyContext);

  async function loadData() {
    const response = await api.get('TipoServico/');
    setTiposServico(response.data);
  }

  useEffect(() => {
    loadData();
  }, []);

  const formik = useFormik({
    initialValues: { tituloTipo: '', codigoServicoSisdm: '' },
    validationSchema,
    onSubmit: async (values) => {
      if (editIndex === null) return;
      const id = tiposServico[editIndex].id;
      const payload = {
        tituloTipo: values.tituloTipo,
        codigoServicoSisdm: Number(values.codigoServicoSisdm),
      };

      try {
        if (id !== 0) {
          await api.put(`/TipoServico/`, { id, ...payload });
        } else {
          await api.post('/TipoServico/', { id: 0, ...payload });
        }

        await loadData();
        setEditIndex(null);
        formik.resetForm();
      } catch (error) {
        const msg = error?.response?.data || error.message || 'Erro ao salvar o tipo de serviço.';
        setMensagemGlobal(msg);
        setTipoMensagemGlobal('danger');
      }
    },
  });

  const handleEdit = (index) => {
    setEditIndex(index);
    formik.resetForm();
    formik.setFieldValue('tituloTipo', tiposServico[index].tituloTipo);
    formik.setFieldValue('codigoServicoSisdm', tiposServico[index].codigoServicoSisdm ?? '');
  };

  const handleCancelEdit = () => {
    if (tiposServico[editIndex]?.id === 0) {
      setTiposServico(tiposServico.filter((_, i) => i !== editIndex));
    }
    setEditIndex(null);
    formik.resetForm();
  };

  const handleDelete = async (id) => {
    await api.delete(`/TipoServico/${id}`);
    setTiposServico(tiposServico.filter((t) => t.id !== id));
  };

  const handleAddTipo = () => {
    if (editIndex !== null) return;
    const newList = [...tiposServico, { id: 0, tituloTipo: '' }];
    setTiposServico(newList);
    setEditIndex(newList.length - 1);
    formik.resetForm();
  };

  return (
    <Container sx={{ mt: 2 }}>
      {mensagemGlobal && <Mensagem mensagem={mensagemGlobal} setMensagem={setMensagemGlobal} variant={tipoMensagemGlobal} />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
        <Typography variant="h5" gutterBottom>
          Tipos de Serviço
        </Typography>

        <Tooltip title={editIndex !== null ? 'Salve ou cancele a edição antes de adicionar um novo tipo' : ''}>
          <span>
            <Button
              variant="text"
              disabled={editIndex !== null}
              sx={{ height: '2.5rem', fontSize: '0.78rem', color: 'rgb(19, 35, 80)', display: 'flex', alignItems: 'end' }}
              onClick={handleAddTipo}
            >
              <AddCircleIcon sx={{ marginRight: '0.5rem' }} />
              Novo Tipo
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Paper elevation={2} sx={{ marginY: 1, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ width: '60px' }}>ID</StyledTableCell>
                  <StyledTableCell>Título do Tipo</StyledTableCell>
                  <StyledTableCell sx={{ width: '180px' }}>Cód. Serviço SISDM</StyledTableCell>
                  <StyledTableCell align="right" sx={{ width: '100px' }}>Ações</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiposServico.map((tipo, index) => (
                  <StyledTableRow
                    key={tipo.id ?? `new-${index}`}
                    sx={editIndex === index ? { backgroundColor: '#f0f7ff' } : {}}
                  >
                    <TableCell sx={{ verticalAlign: 'middle', py: 1.5 }}>{tipo.id || '-'}</TableCell>

                    <TableCell sx={{ verticalAlign: 'middle', py: 1.5 }}>
                      {editIndex === index ? (
                        <Tooltip
                          title={formik.touched.tituloTipo && formik.errors.tituloTipo ? formik.errors.tituloTipo : ''}
                          open={!!(formik.touched.tituloTipo && formik.errors.tituloTipo)}
                          placement="bottom-start"
                          arrow
                        >
                          <TextField
                            size="small"
                            name="tituloTipo"
                            value={formik.values.tituloTipo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tituloTipo && Boolean(formik.errors.tituloTipo)}
                            placeholder="Título do tipo"
                            fullWidth
                            autoFocus
                            sx={{ minWidth: '200px' }}
                          />
                        </Tooltip>
                      ) : (
                        tipo.tituloTipo
                      )}
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'middle', py: 1.5 }}>
                      {editIndex === index ? (
                        <Tooltip
                          title={formik.touched.codigoServicoSisdm && formik.errors.codigoServicoSisdm ? formik.errors.codigoServicoSisdm : ''}
                          open={!!(formik.touched.codigoServicoSisdm && formik.errors.codigoServicoSisdm)}
                          placement="bottom-start"
                          arrow
                        >
                          <TextField
                            size="small"
                            type="number"
                            name="codigoServicoSisdm"
                            value={formik.values.codigoServicoSisdm}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.codigoServicoSisdm && Boolean(formik.errors.codigoServicoSisdm)}
                            placeholder="sdm026"
                            sx={{ width: '130px' }}
                            inputProps={{ min: 1, max: 99999 }}
                          />
                        </Tooltip>
                      ) : (
                        tipo.codigoServicoSisdm ?? '-'
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ verticalAlign: 'middle', py: 1.5 }}>
                      {editIndex === index ? (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Salvar">
                            <IconButton onClick={formik.handleSubmit} size="small">
                              <Check color="success" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton onClick={handleCancelEdit} size="small">
                              <Close color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Editar">
                            <span>
                              <IconButton
                                onClick={() => handleEdit(index)}
                                disabled={editIndex !== null}
                                size="small"
                              >
                                <Edit style={{ color: editIndex !== null ? '#ccc' : '#ffc107' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Deletar">
                            <span>
                              <IconButton
                                onClick={() => handleDelete(tipo.id)}
                                disabled={editIndex !== null}
                                size="small"
                              >
                                <Delete color={editIndex !== null ? 'disabled' : 'error'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}
